import { useState, useEffect, useCallback, useMemo } from 'react';
import { Routes, Route, NavLink, Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import {
  LogOut, Mail, HelpCircle, ExternalLink,
  CheckCircle2, Circle, Zap, Users, KeyRound,
  Plug, CreditCard, ShieldCheck,
  GitBranch, UserPlus, Copy, Check, CircleUser,
  Building2, User, Star,
} from 'lucide-react';
import { usePermissions } from '@core/auth/usePermissions';
import { useAuth } from '@core/auth/useAuth';
import { orgService } from '@core/supabase/services/orgService';
import { PrivateStorageAvatar } from '@shared/components/ui/PrivateStorageAvatar';
import { Organization, OrgRole, OrgUser } from '@core/types';
import { PermissionGate } from '@shared/components/ui/PermissionGate';
import { CandidateCollapsiblePanel } from '@modules/candidates/CandidateCollapsiblePanel';
import { MaskingTemplatesPage } from './MaskingTemplatesPage';
import { EmailTemplatesSettingsPage } from './EmailTemplatesSettingsPage';
import { OrgSettingsPage } from './OrgSettingsPage';
import { BillingPage } from './BillingPage';
import { IntegrationsPage } from './IntegrationsPage';
import { ProfilePage } from './ProfilePage';
import { OrgHierarchyPage, type MemberWithStats } from './OrgHierarchyPage';
import { AutomationSettingsPage } from './AutomationSettingsPage';
import { RolesSettingsPage } from './RolesSettingsPage';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';

const BRAND = '#8B80F9';

/** Settings side rails — match Profile account tab: 0.875rem, label semibold, value regular */
const SETTINGS_K = 'text-sm font-semibold text-black dark:text-zinc-300 shrink-0';
const SETTINGS_V = 'text-sm font-normal vscode-text-primary text-right break-words min-w-0';

const PLAN_COLOR: Record<string, string> = {
  starter: '#94a3b8', team: '#22c55e', agency: BRAND, enterprise: '#f59e0b',
};
const ROLE_LABEL: Record<OrgRole, string> = {
  owner: 'Owner', admin: 'Admin', senior_recruiter: 'Senior Recruiter',
  junior_recruiter: 'Junior Recruiter', viewer: 'Viewer',
};
const ROLE_COLOR: Record<OrgRole, string> = {
  owner: '#f59e0b', admin: BRAND, senior_recruiter: '#22c55e',
  junior_recruiter: '#3b82f6', viewer: '#94a3b8',
};

// ── Tab definition (no icons, ordered: Profile first) ──────────
type NavItem = { to: string; label: string; end: boolean };
const BASE_NAV: NavItem[] = [
  { to: '/settings/profile',        label: 'Profile',           end: false },
  { to: '/settings/team',           label: 'Team',              end: false },
  { to: '/settings/roles',         label: 'Roles', end: false },
  { to: '/settings/branding',      label: 'Branding', end: false },
  { to: '/settings/integrations', label: 'Integrations', end: false },
  { to: '/settings/automation',    label: 'Automation', end: false },
  { to: '/settings/billing',       label: 'Billing', end: false },
];

function nameInitials(name: string | null | undefined): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'U';
  return parts.length === 1 ? parts[0][0].toUpperCase() : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ── Context sidebar — uses CandidateCollapsiblePanel ──────────
function ContextSidebar({
  tab, org, user, perms, navigate, logout, selectedOrgMember, meOrgUser,
  onOrgHierarchyAddMember,
}: {
  tab: string;
  org: Organization | null;
  user: ReturnType<typeof useAuth>['user'];
  perms: ReturnType<typeof usePermissions>;
  navigate: (to: string) => void;
  logout: () => void;
  selectedOrgMember: MemberWithStats | null;
  meOrgUser: OrgUser | null;
  onOrgHierarchyAddMember?: () => void;
}) {
  const [orgInviteCopied, setOrgInviteCopied] = useState(false);
  const role = user?.roles?.[0] as OrgRole | undefined;
  const planColor = PLAN_COLOR[org?.plan ?? ''] ?? BRAND;

  const orgInviteLink = useMemo(
    () => (typeof window !== 'undefined' && user?.orgId
      ? `${window.location.origin}/onboarding?orgId=${user.orgId}`
      : ''),
    [user?.orgId],
  );

  // ── Profile ──
  if (tab === 'profile') {
    return (
      <>
        <CandidateCollapsiblePanel storageKey="settings-profile-identity" title="Your identity" defaultOpen>
          <div className="space-y-3 px-1 gg-app-body">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-sm font-bold overflow-hidden" style={{ background: `${BRAND}20`, color: BRAND }}>
                {meOrgUser?.avatar_url ? (
                  <PrivateStorageAvatar
                    key={meOrgUser.avatar_url}
                    path={meOrgUser.avatar_url}
                    className="w-full h-full object-cover"
                    fallback={<span className="text-sm font-bold">{nameInitials(user?.name)}</span>}
                  />
                ) : (
                  nameInitials(user?.name)
                )}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold vscode-text-primary truncate">{user?.name ?? '—'}</p>
                <p className="gg-app-body-muted text-xs truncate">{user?.email}</p>
              </div>
            </div>
            <div className="space-y-1.5">
              {[
                ['Organisation', user?.orgName ?? '—'],
                ['Role', role ? ROLE_LABEL[role] : '—'],
                ['AI Credits', String(user?.credits?.toLocaleString() ?? '—')],
              ].map(([k, v]) => (
                <div key={k} className="flex items-start justify-between gap-2 py-1.5 border-b border-[var(--vscode-border-primary)]/30 last:border-0">
                  <span className={SETTINGS_K}>{k}</span>
                  <span className={SETTINGS_V}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </CandidateCollapsiblePanel>

        <CandidateCollapsiblePanel storageKey="settings-profile-security" title="Account actions" defaultOpen>
          <div className="space-y-1 -mx-4 -mb-3">
            <a href="https://auth.gograbjob.com/realms/b2b/account/password" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm vscode-text-secondary hover:vscode-text-primary hover:vscode-bg-hover transition-colors border-b border-[var(--vscode-border-primary)]/30">
              <KeyRound className="w-3.5 h-3.5 shrink-0 vscode-text-tertiary" />
              Change password
              <ExternalLink className="w-3 h-3 ml-auto vscode-text-tertiary" />
            </a>
            <a href="https://auth.gograbjob.com/realms/b2b/account" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm vscode-text-secondary hover:vscode-text-primary hover:vscode-bg-hover transition-colors border-b border-[var(--vscode-border-primary)]/30">
              <User className="w-3.5 h-3.5 shrink-0 vscode-text-tertiary" />
              Keycloak account
              <ExternalLink className="w-3 h-3 ml-auto vscode-text-tertiary" />
            </a>
            <button type="button" onClick={logout}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-400 hover:bg-red-50/60 dark:hover:bg-red-950/30 transition-colors text-left">
              <LogOut className="w-3.5 h-3.5 shrink-0" /> Sign out
            </button>
          </div>
        </CandidateCollapsiblePanel>
      </>
    );
  }

  // ── Organisation / Branding ──
  if (tab === 'organisation' || tab === 'branding') {
    return (
      <>
        <CandidateCollapsiblePanel storageKey="settings-org-plan" title="Plan & Limits" defaultOpen>
          <div className="space-y-1.5 px-1">
            {[
              ['Plan',          <span className="capitalize font-normal" style={{ color: planColor }}>{org?.plan ?? '—'}</span>],
              ['Seats',         String(org?.seats_allocated ?? '—')],
              ['Credits',       <span className="font-normal" style={{ color: BRAND }}>{org?.credits_balance?.toLocaleString() ?? '—'}</span>],
              ['Billing email', org?.billing_email ?? '—'],
              ['GST',           org?.gst_number ?? '—'],
            ].map(([k, v]) => (
              <div key={String(k)} className="flex items-start justify-between gap-2 py-1 border-b border-[var(--vscode-border-primary)]/30 last:border-0">
                <span className={SETTINGS_K}>{String(k)}</span>
                <span className={SETTINGS_V}>{v as React.ReactNode}</span>
              </div>
            ))}
          </div>
        </CandidateCollapsiblePanel>

        <CandidateCollapsiblePanel storageKey="settings-org-compliance" title="Compliance" defaultOpen>
          <div className="space-y-1.5 px-1">
            {[
              ['Retention (days)', String(org?.retention_days ?? '—')],
              ['Consent version',  org?.consent_version ?? '—'],
              ['GDPR DPA', org?.gdpr_dpa_signed
                ? <span className="text-green-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Yes</span>
                : <span className="vscode-text-tertiary flex items-center gap-1"><Circle className="w-3 h-3" /> No</span>],
            ].map(([k, v]) => (
              <div key={String(k)} className="flex items-start justify-between gap-2 py-1 border-b border-[var(--vscode-border-primary)]/30 last:border-0">
                <span className={SETTINGS_K}>{String(k)}</span>
                <span className={SETTINGS_V}>{v as React.ReactNode}</span>
              </div>
            ))}
          </div>
        </CandidateCollapsiblePanel>

        <CandidateCollapsiblePanel storageKey="settings-org-quicklinks" title="Quick links" defaultOpen>
          <div className="-mx-4 -mb-3">
            {[
              { icon: Plug,       label: 'Integrations',  to: '/settings/integrations'  },
              { icon: GitBranch,  label: 'Team', to: '/settings/team' },
              { icon: CreditCard, label: 'Billing',       to: '/settings/billing'       },
            ].map(({ icon: Icon, label, to }) => (
              <Link key={to} to={to}
                className="flex items-center gap-2.5 px-4 py-2.5 text-sm vscode-text-secondary hover:vscode-text-primary hover:vscode-bg-hover transition-colors border-b border-[var(--vscode-border-primary)]/30 last:border-0">
                <Icon className="w-3.5 h-3.5 shrink-0 vscode-text-tertiary" /> {label}
              </Link>
            ))}
            {perms.canManageTeam && (
              <Link to="/settings/offboarding"
                className="flex items-center gap-2.5 px-4 py-2.5 text-sm vscode-text-secondary hover:vscode-text-primary hover:vscode-bg-hover transition-colors border-b border-[var(--vscode-border-primary)]/30 last:border-0">
                <ExternalLink className="w-3.5 h-3.5 shrink-0 vscode-text-tertiary" /> Tenant exit
              </Link>
            )}
          </div>
        </CandidateCollapsiblePanel>
      </>
    );
  }

  // ── Team (org chart) ──
  if (tab === 'team' || tab === 'org-hierarchy') {
    const copyOrgInvite = () => {
      if (!orgInviteLink) return;
      void navigator.clipboard.writeText(orgInviteLink);
      setOrgInviteCopied(true);
      setTimeout(() => setOrgInviteCopied(false), 2000);
    };

    return (
      <>
        <CandidateCollapsiblePanel storageKey="settings-orghier-team" title="Team" defaultOpen>
          <div className="space-y-3 px-1">
            <div>
              <p className="mb-1.5 text-[10px] font-semibold tracking-wide vscode-text-tertiary">Invite link</p>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                <input
                  readOnly
                  className="input w-full min-w-0 truncate text-xs font-mono"
                  value={orgInviteLink}
                  title={orgInviteLink}
                />
                <button
                  type="button"
                  onClick={copyOrgInvite}
                  className="btn-secondary flex shrink-0 items-center justify-center gap-1.5 px-3 text-xs whitespace-nowrap"
                >
                  {orgInviteCopied ? <><Check className="h-3.5 w-3.5 text-green-400" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
                </button>
              </div>
              <p className="mt-1.5 text-[10px] leading-snug vscode-text-tertiary">
                Share with people joining your org via onboarding.
              </p>
            </div>
            {perms.canManageTeam && (
              <button
                type="button"
                onClick={() => onOrgHierarchyAddMember?.()}
                className="btn-primary flex w-full items-center justify-center gap-2 py-2.5 text-sm"
              >
                <UserPlus className="h-4 w-4" /> Add member
              </button>
            )}
          </div>
        </CandidateCollapsiblePanel>

        <CandidateCollapsiblePanel
          storageKey="settings-orghier-apps"
          title={selectedOrgMember
            ? `Applications by ${selectedOrgMember.display_name ?? selectedOrgMember.email.split('@')[0]}`
            : 'Applications by selected subordinate'}
          defaultOpen
        >
          {selectedOrgMember ? (
            <div className="space-y-3 px-1">
              <p className="text-xs vscode-text-secondary leading-relaxed">
                <span className="font-medium vscode-text-primary">{selectedOrgMember.stats.applications}</span> assigned ·{' '}
                <span className="font-medium vscode-text-primary">{selectedOrgMember.stats.placed}</span> placed ·{' '}
                <span className="font-medium vscode-text-primary">{selectedOrgMember.stats.candidates}</span> candidates
              </p>
              <Link
                to={`/applications?assignedTo=${selectedOrgMember.id}`}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg border vscode-border bg-[color-mix(in_srgb,var(--vscode-bg-tertiary)_35%,transparent)] px-3 py-2.5 text-sm font-medium vscode-text-primary transition-colors hover:vscode-bg-hover"
              >
                Open applications
              </Link>
            </div>
          ) : (
            <p className="px-1 text-xs leading-relaxed vscode-text-tertiary">
              Select someone in the org chart to filter applications to that team member.
            </p>
          )}
        </CandidateCollapsiblePanel>

        {selectedOrgMember && (
          <CandidateCollapsiblePanel storageKey="settings-orghier-member" title="Member details" defaultOpen>
            {(() => {
              const m = selectedOrgMember;
              const meta = {
                owner:            { color: '#f59e0b', label: 'Owner' },
                admin:            { color: BRAND,     label: 'Admin' },
                senior_recruiter: { color: '#22c55e', label: 'Senior Recruiter' },
                junior_recruiter: { color: '#3b82f6', label: 'Junior Recruiter' },
                viewer:           { color: '#94a3b8', label: 'Viewer' },
              }[m.role];
              const initials = (m.display_name ?? m.email).split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();
              return (
                <div className="space-y-3 px-1">
                  <div className="flex items-center gap-3">
                    <div
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold"
                      style={{ background: `${meta.color}20`, color: meta.color }}
                    >
                      {initials}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold vscode-text-primary">{m.display_name ?? '—'}</p>
                      <span
                        className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
                        style={{ background: `${meta.color}15`, color: meta.color }}
                      >
                        {meta.label}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {[
                      ['Email', m.email],
                      ['Status', m.is_active ? <span className="text-green-400">Active</span> : <span className="text-red-400">Inactive</span>],
                      ['Joined', m.joined_at ? formatDateTimeGmt(m.joined_at) : '—'],
                    ].map(([k, v]) => (
                      <div
                        key={String(k)}
                        className="flex items-start justify-between gap-2 border-b border-[var(--vscode-border-primary)]/30 py-1 last:border-0"
                      >
                        <span className={SETTINGS_K}>{String(k)}</span>
                        <span className={`${SETTINGS_V} break-all`}>{v as React.ReactNode}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </CandidateCollapsiblePanel>
        )}
      </>
    );
  }

  // ── Integrations ──
  if (tab === 'integrations') {
    return (
      <>
        <CandidateCollapsiblePanel storageKey="settings-int-quick" title="Quick links" defaultOpen>
          <div className="-mx-4 -mb-3">
            <a
              href="mailto:support@gograbjob.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm vscode-text-secondary hover:vscode-text-primary hover:vscode-bg-hover transition-colors border-b border-[var(--vscode-border-primary)]/30"
            >
              <Mail className="w-3.5 h-3.5 shrink-0 vscode-text-tertiary" />
              Email support
              <ExternalLink className="w-3 h-3 ml-auto vscode-text-tertiary" />
            </a>
            <a
              href="https://docs.gograbjob.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm vscode-text-secondary hover:vscode-text-primary hover:vscode-bg-hover transition-colors"
            >
              <HelpCircle className="w-3.5 h-3.5 shrink-0 vscode-text-tertiary" />
              Docs
              <ExternalLink className="w-3 h-3 ml-auto vscode-text-tertiary" />
            </a>
          </div>
        </CandidateCollapsiblePanel>

        <CandidateCollapsiblePanel storageKey="settings-int-connectors" title="Available Connectors" defaultOpen>
          <div className="space-y-2 px-1">
            {[
              { label: 'SMTP / Email',    desc: 'Outbound email sender'     },
              { label: 'Slack',           desc: 'App notifications'          },
              { label: 'Google Calendar', desc: 'Interview event sync'        },
              { label: 'LinkedIn Jobs',   desc: 'One-click job posting'       },
              { label: 'Groq AI',         desc: 'LLM model key override'      },
            ].map(({ label, desc }) => (
              <div key={label} className="flex items-start gap-2 py-1 border-b border-[var(--vscode-border-primary)]/30 last:border-0">
                <Plug className="w-3 h-3 shrink-0 mt-0.5" style={{ color: BRAND }} />
                <div>
                  <p className="text-xs font-medium vscode-text-primary">{label}</p>
                  <p className="text-[10px] vscode-text-tertiary">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </CandidateCollapsiblePanel>
      </>
    );
  }

  // ── Masking ──
  if (tab === 'masking') {
    return (
      <>
        <CandidateCollapsiblePanel storageKey="settings-mask-quick" title="Quick links" defaultOpen>
          <div className="-mx-4 -mb-3">
            <Link to="/settings/integrations"
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm vscode-text-secondary hover:vscode-text-primary hover:vscode-bg-hover transition-colors border-b border-[var(--vscode-border-primary)]/30">
              <Plug className="w-3.5 h-3.5 shrink-0 vscode-text-tertiary" /> Integrations
            </Link>
            <a href="https://docs.gograbjob.com/masking" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm vscode-text-secondary hover:vscode-text-primary hover:vscode-bg-hover transition-colors">
              <HelpCircle className="w-3.5 h-3.5 shrink-0 vscode-text-tertiary" />
              Masking docs
              <ExternalLink className="w-3 h-3 ml-auto vscode-text-tertiary" />
            </a>
          </div>
        </CandidateCollapsiblePanel>

        <CandidateCollapsiblePanel storageKey="settings-mask-about" title="What is Masking?" defaultOpen>
          <div className="space-y-2 px-1">
            <p className="text-xs vscode-text-secondary leading-relaxed">
              Masking templates control which candidate fields are hidden when sharing profiles with clients. Set a default to apply automatically.
            </p>
          </div>
        </CandidateCollapsiblePanel>

        <CandidateCollapsiblePanel storageKey="settings-mask-tips" title="Best Practices" defaultOpen>
          <div className="space-y-2 px-1">
            {[
              'Always mask mobile numbers before sharing',
              'Create sector-specific templates (BFSI, IT)',
              'Set a default to avoid accidental disclosure',
            ].map((text) => (
              <div key={text} className="flex items-start gap-2 py-1 border-b border-[var(--vscode-border-primary)]/30 last:border-0">
                <ShieldCheck className="w-3 h-3 shrink-0 mt-0.5" style={{ color: BRAND }} />
                <p className="text-xs vscode-text-secondary">{text}</p>
              </div>
            ))}
          </div>
        </CandidateCollapsiblePanel>
      </>
    );
  }

  // ── Email templates ──
  if (tab === 'email-templates') {
    return (
      <>
        <CandidateCollapsiblePanel storageKey="settings-email-tpl-quick" title="Quick links" defaultOpen>
          <div className="-mx-4 -mb-3">
            <Link
              to="/emails"
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm vscode-text-secondary hover:vscode-text-primary hover:vscode-bg-hover transition-colors border-b border-[var(--vscode-border-primary)]/30"
            >
              <Mail className="w-3.5 h-3.5 shrink-0 vscode-text-tertiary" /> Email activity
            </Link>
            <Link
              to="/emails/compose"
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm vscode-text-secondary hover:vscode-text-primary hover:vscode-bg-hover transition-colors"
            >
              <Mail className="w-3.5 h-3.5 shrink-0 vscode-text-tertiary" /> Compose
            </Link>
          </div>
        </CandidateCollapsiblePanel>
        <CandidateCollapsiblePanel storageKey="settings-email-tpl-about" title="Compose parity" defaultOpen>
          <p className="text-xs vscode-text-secondary leading-relaxed px-1">
            The template catalogue in Settings matches the grouped dropdown in Pipeline → Compose, so recruiters see one consistent list of
            built-ins plus organisation templates.
          </p>
        </CandidateCollapsiblePanel>
      </>
    );
  }

  // ── Roles (RBAC overview) ──
  if (tab === 'roles') {
    return (
      <>
        <CandidateCollapsiblePanel storageKey="settings-roles-team" title="Assign roles" defaultOpen>
          <p className="text-xs vscode-text-secondary px-1 leading-relaxed">
            Open <Link to="/settings/team" className="text-brand-400 hover:underline">Team</Link> to change a member&apos;s
            role. You&apos;ll see a preview of which areas they gain or lose before confirming.
          </p>
        </CandidateCollapsiblePanel>
      </>
    );
  }

  // ── Automation ──
  if (tab === 'automation') {
    return (
      <>
        <CandidateCollapsiblePanel storageKey="settings-auto-tip" title="How it works" defaultOpen>
          <p className="text-xs vscode-text-secondary px-1 leading-relaxed">
            Rules here use the same org automation settings as Smart Match after a score is saved. Adjust thresholds and
            toggles on the left; changes save immediately.
          </p>
        </CandidateCollapsiblePanel>
      </>
    );
  }

  // ── Billing ──
  if (tab === 'billing') {
    return (
      <>
        <CandidateCollapsiblePanel storageKey="settings-billing-quick" title="Quick links" defaultOpen>
          <div className="-mx-4 -mb-3">
            <a
              href="mailto:business@gograbjob.com"
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm vscode-text-secondary hover:vscode-text-primary hover:vscode-bg-hover transition-colors border-b border-[var(--vscode-border-primary)]/30"
            >
              <Mail className="w-3.5 h-3.5 shrink-0 vscode-text-tertiary" />
              Contact sales
            </a>
            <Link
              to="/settings/branding"
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm vscode-text-secondary hover:vscode-text-primary hover:vscode-bg-hover transition-colors"
            >
              <Building2 className="w-3.5 h-3.5 shrink-0 vscode-text-tertiary" />
              Branding & org
            </Link>
          </div>
        </CandidateCollapsiblePanel>

        <CandidateCollapsiblePanel storageKey="settings-billing-plan" title="Current Plan" defaultOpen>
          <div className="space-y-1.5 px-1">
            {[
              ['Plan',          <span className="capitalize font-normal" style={{ color: planColor }}>{org?.plan ?? '—'}</span>],
              ['Seats',         String(org?.seats_allocated ?? '—')],
              ['Credits',       <span className="font-normal" style={{ color: BRAND }}>{org?.credits_balance?.toLocaleString() ?? '—'}</span>],
              ['Billing email', org?.billing_email ?? '—'],
            ].map(([k, v]) => (
              <div key={String(k)} className="flex items-start justify-between gap-2 py-1 border-b border-[var(--vscode-border-primary)]/30 last:border-0">
                <span className={SETTINGS_K}>{String(k)}</span>
                <span className={SETTINGS_V}>{v as React.ReactNode}</span>
              </div>
            ))}
          </div>
        </CandidateCollapsiblePanel>

        <CandidateCollapsiblePanel storageKey="settings-billing-contact" title="Upgrade / Contact" defaultOpen>
          <div className="space-y-2 px-1">
            <p className="text-xs vscode-text-secondary">Contact us to upgrade or buy credits.</p>
            <a href="mailto:business@gograbjob.com"
              className="inline-flex items-center gap-1.5 text-xs font-medium rounded-lg px-3 py-2 border vscode-border hover:vscode-bg-hover"
              style={{ color: BRAND }}>
              <Mail className="w-3.5 h-3.5" /> business@gograbjob.com
            </a>
          </div>
        </CandidateCollapsiblePanel>
      </>
    );
  }

  // ── Fallback ──
  return (
    <CandidateCollapsiblePanel storageKey="settings-default-account" title="Summary" defaultOpen>
      <div className="space-y-1.5 px-1">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 overflow-hidden" style={{ background: `${BRAND}20`, color: BRAND }}>
            {meOrgUser?.avatar_url ? (
              <PrivateStorageAvatar
                path={meOrgUser.avatar_url}
                className="w-full h-full object-cover"
                fallback={<span className="text-sm font-bold">{nameInitials(user?.name)}</span>}
              />
            ) : (
              <span className="text-sm font-bold">{nameInitials(user?.name)}</span>
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold vscode-text-primary truncate">{user?.name ?? 'User'}</p>
            <p className="text-xs vscode-text-tertiary truncate">{user?.email}</p>
          </div>
        </div>
        {[
          ['Organisation', user?.orgName ?? '—'],
          ['Role', role ? ROLE_LABEL[role] : '—'],
        ].map(([k, v]) => (
          <div key={k} className="flex items-start justify-between gap-2 py-1 border-b border-[var(--vscode-border-primary)]/30 last:border-0">
            <span className={SETTINGS_K}>{k}</span>
            <span className={SETTINGS_V}>{v}</span>
          </div>
        ))}
      </div>
    </CandidateCollapsiblePanel>
  );
}

// ── Main ───────────────────────────────────────────────────────

export function SettingsPage() {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const perms = usePermissions();
  const navigate = useNavigate();
  const [org, setOrg]                             = useState<Organization | null>(null);
  const [selectedOrgMember, setSelectedOrgMember] = useState<MemberWithStats | null>(null);
  const [meOrgUser, setMeOrgUser]                 = useState<OrgUser | null>(null);
  const [orgAddMemberOpen, setOrgAddMemberOpen]   = useState(false);

  const refreshMeOrgUser = useCallback(() => {
    if (!user?.orgId) {
      setMeOrgUser(null);
      return;
    }
    orgService
      .getTeamMembers(user.orgId)
      .then((members) => {
        const me = members.find((m) => m.keycloak_sub === user.id || m.email === user.email);
        setMeOrgUser(me ?? null);
      })
      .catch(() => setMeOrgUser(null));
  }, [user?.orgId, user?.id, user?.email]);

  useEffect(() => {
    if (user?.orgId) orgService.getOrg(user.orgId).then(setOrg).catch(() => setOrg(null));
  }, [user?.orgId]);

  useEffect(() => {
    refreshMeOrgUser();
  }, [refreshMeOrgUser]);

  useEffect(() => {
    if (!pathname.startsWith('/settings/team') && !pathname.startsWith('/settings/org-hierarchy')) {
      setSelectedOrgMember(null);
    }
  }, [pathname]);

  const activeItem = BASE_NAV.find(({ to, end }) => end ? pathname === to : pathname.startsWith(to));

  const tabKey =
    pathname.startsWith('/settings/profile')        ? 'profile'       :
    pathname.startsWith('/settings/team')           ? 'team'          :
    pathname.startsWith('/settings/org-hierarchy')  ? 'team'          :
    pathname.startsWith('/settings/roles')          ? 'roles'         :
    pathname.startsWith('/settings/branding')       ? 'branding'      :
    pathname.startsWith('/settings/organisation')  ? 'branding'      :
    pathname.startsWith('/settings/integrations')   ? 'integrations'  :
    pathname.startsWith('/settings/automation')     ? 'automation'    :
    pathname.startsWith('/settings/masking')        ? 'masking'       :
    pathname.startsWith('/settings/email-templates') ? 'email-templates' :
    pathname.startsWith('/settings/billing')        ? 'billing'       :
    'profile';

  return (
    <div className="w-full pb-8 ggj-settings-page">

      {/* ── Page header ── */}
      <PageHeader
        icon={CircleUser}
        className="!mb-3"
        breadcrumb={
          activeItem && activeItem.to !== '/settings/profile'
            ? [
                { label: 'Account', href: '/settings/profile' },
                { label: activeItem.label },
              ]
            : undefined
        }
        title="Account"
        subtitle={user?.orgName ? `${user.orgName} · ${user.email ?? ''}` : (user?.email ?? '')}
      />

      {/* ── Tab bar + profile actions flush under “Profile” tab ── */}
      <div className="mb-4">
        <nav className="ggj-cand-tablist mb-0" aria-label="Account section tabs">
          {BASE_NAV.map(({ to, label, end }) => {
            const isActive = end ? pathname === to : pathname.startsWith(to);
            return (
              <NavLink key={to} to={to} end={end}
                className={`ggj-cand-tab ${isActive ? 'ggj-cand-tab--active' : ''}`}>
                {label}
              </NavLink>
            );
          })}
        </nav>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] vscode-text-tertiary mt-2">
          <span className="uppercase tracking-wide">Also:</span>
          <Link to="/settings/email-templates" className="hover:vscode-text-primary underline-offset-2 hover:underline">
            Email templates
          </Link>
          <Link to="/settings/masking" className="hover:vscode-text-primary underline-offset-2 hover:underline">
            Masking
          </Link>
        </div>
      </div>

      {/* ── Two-column grid: [content | 340px sidebar] ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 items-start">

        {/* ── LEFT: tab content ── */}
        <div className="min-w-0">
          <Routes>
            <Route index element={<Navigate to="/settings/profile" replace />} />
            <Route path="profile" element={<ProfilePage onAvatarUpdated={refreshMeOrgUser} />} />
            <Route path="organisation" element={<Navigate to="/settings/branding" replace />} />
            <Route path="branding" element={<OrgSettingsPage />} />
            <Route
              path="team"
              element={
                <OrgHierarchyPage
                  onSelectMember={setSelectedOrgMember}
                  addMemberModalOpen={orgAddMemberOpen}
                  onAddMemberModalOpenChange={setOrgAddMemberOpen}
                />
              }
            />
            <Route
              path="org-hierarchy"
              element={<Navigate to="/settings/team" replace />}
            />
            <Route path="roles" element={<RolesSettingsPage />} />
            <Route path="automation" element={<AutomationSettingsPage />} />
            <Route path="integrations" element={<IntegrationsPage />} />
            <Route path="masking" element={
                <PermissionGate allowed={perms.canManageMaskingTemplates} label="Masking templates">
                  <MaskingTemplatesPage />
                </PermissionGate>
            } />
            <Route
              path="email-templates"
              element={
                <PermissionGate
                  allowed={perms.canSendEmail || perms.isAdmin || perms.isOwner}
                  label="Email templates"
                >
                  <EmailTemplatesSettingsPage />
                </PermissionGate>
              }
            />
            <Route path="billing" element={<BillingPage />} />
            <Route path="ai-usage" element={<Navigate to="/settings/profile" replace />} />
          </Routes>
        </div>

        {/* ── RIGHT: 340px sticky context sidebar ── */}
        <aside className="ggj-settings-rail space-y-3 lg:sticky lg:top-4 self-start">
          <ContextSidebar
            tab={tabKey} org={org} user={user} perms={perms}
            navigate={navigate} logout={logout}
            selectedOrgMember={selectedOrgMember}
            meOrgUser={meOrgUser}
            onOrgHierarchyAddMember={() => setOrgAddMemberOpen(true)}
          />
        </aside>

      </div>
    </div>
  );
}
